import { useCallback, useEffect, useState } from "react";
import Spinner from "./../ui/Spinner";
import { ErrorState, Help, HowItWorks, ModalShell, PersonCell, btnGhost, btnPrimary, errMsg } from "./promotionsUi";
import { SKIP_LABEL, Stat, type Toast } from "./campaignUi";
import { promotionsService } from "../../features/promotions/service";
import { formatISTDateTime } from "../../utils/dateUtils";
import type { ApplyProgress, ApplyStatus, AudiencePreview, Campaign, CampaignApplyApproval, PromoRole } from "../../features/promotions/types";

// ── Row actions: preview / apply / progress ─────────────────────────────────
const APPLY_BADGE: Record<ApplyStatus, { cls: string; label: string }> = {
  idle: { cls: "bg-gray-700 text-gray-300", label: "Not applied" },
  running: { cls: "bg-blue-900/60 text-blue-300", label: "Running" },
  done: { cls: "bg-emerald-900/60 text-emerald-300", label: "Done" },
  failed: { cls: "bg-red-900/50 text-red-300", label: "Failed" },
};

const APPLY_ERROR_TEXT: Record<string, string> = {
  campaign_not_live: "Stopped because the campaign was no longer live. Credits already granted stay.",
  budget_reached: "Stopped because the confirmed total was reached.",
};

export function applyErrorText(err?: string | null): string {
  if (!err) return "Stopped before finishing. Credits already granted stay.";
  return APPLY_ERROR_TEXT[err] ?? `Stopped: ${err}. Credits already granted stay.`;
}

export function ApplyStatusBadge({ status, error }: { status?: ApplyStatus; error?: string | null }) {
  const b = APPLY_BADGE[status ?? "idle"] ?? APPLY_BADGE.idle;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${b.cls}`} title={status === "failed" ? applyErrorText(error) : undefined}>
      {b.label}
    </span>
  );
}

export function AudienceModal({ campaign, onClose, onChanged, toast }: { campaign: Campaign; onClose: () => void; onChanged: () => void; toast: Toast }) {
  const claim = campaign.grant_mode === "claim";
  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState<ApplyProgress | null>(null);
  const [status, setStatus] = useState<ApplyStatus>(campaign.apply_status ?? "idle");
  const [conflict, setConflict] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [fresh, setFresh] = useState<Campaign>(campaign);
  // A large apply waits for a different admin (Gamification > Approvals).
  const [approval, setApproval] = useState<CampaignApplyApproval | null>(null);

  const loadApproval = useCallback(async () => {
    try {
      const p = await promotionsService.campaignProgress(campaign.id);
      setApproval(p.approval ?? null);
      if (p.status === "running") setStatus("running"); // approved meanwhile: follow the run
    } catch {
      /* keep the last known state */
    }
  }, [campaign.id]);

  useEffect(() => { void loadApproval(); }, [loadApproval]);

  const refreshCampaign = useCallback(async () => {
    try {
      const rows = await promotionsService.listCampaigns();
      const c = rows.find((x) => x.id === campaign.id);
      if (c) setFresh(c);
    } catch {
      /* keep the last known values */
    }
  }, [campaign.id]);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setPreviewError(null);
    try {
      setPreview(await promotionsService.audiencePreview(campaign.id));
    } catch (e) {
      // Kept inline (not only a toast): a too-large audience answers 503 with advice.
      setPreviewError(errMsg(e, "Failed to preview audience"));
    } finally {
      setLoading(false);
    }
  }, [campaign.id, toast]);

  useEffect(() => { void loadPreview(); }, [loadPreview]);

  // Poll progress every 2s while running.
  useEffect(() => {
    if (status !== "running") return;
    let stop = false;
    const tick = async () => {
      try {
        const p = await promotionsService.campaignProgress(campaign.id);
        if (stop) return;
        setProgress(p);
        if (p.status !== "running") {
          setStatus(p.status);
          onChanged();
          void refreshCampaign();
          void loadPreview();
        }
      } catch {
        /* keep polling */
      }
    };
    void tick();
    const t = setInterval(tick, 2000);
    return () => { stop = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, campaign.id]);

  const users = preview?.eligible ?? 0;
  const total = preview?.total_credits ?? 0;
  const perRole = preview?.credits;
  const roleParts = (["seller", "buyer"] as PromoRole[])
    .map((r) => ({ r, n: preview?.by_role?.[r]?.eligible ?? 0, each: preview?.by_role?.[r]?.credits_each ?? 0 }))
    .filter((x) => x.n > 0);
  const breakdown = roleParts.map((x) => `${x.n.toLocaleString("en-IN")} ${x.r}s x ${x.each.toLocaleString("en-IN")}`).join(" + ");

  const doApply = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      const res = await promotionsService.applyCampaign(campaign.id, preview.total_credits);
      setConfirming(false);
      setConflict(null);
      if (res.status === "approval_required") {
        toast.showSuccess("This apply is large, so a different admin has to approve it in Gamification > Approvals. Nothing has been paid yet.");
        await loadApproval();
        return;
      }
      setStatus("running");
      toast.showSuccess(claim ? "Sending claim notices…" : "Applying credits…");
      onChanged();
    } catch (e) {
      const code = (e as { response?: { status?: number } })?.response?.status;
      if (code === 409) {
        setConflict(`${errMsg(e, "The audience or cost changed")}. The preview was refreshed - review the new numbers and confirm again.`);
        setConfirming(false);
        await loadPreview();
      } else {
        toast.showError(errMsg(e, "Failed to apply"));
      }
    } finally {
      setApplying(false);
    }
  };

  // Admin-apply: granted+failed of total. Claim mode: notified (or still-pending claims) of total.
  // A user who is both granted and notified is counted once. 100% only when the run is done.
  const done = claim ? Math.max(progress?.notified ?? 0, progress?.pending ?? 0) + (progress?.failed ?? 0) : (progress?.granted ?? 0) + (progress?.failed ?? 0);
  const rawPct = progress && progress.total > 0 ? Math.round((Math.min(done, progress.total) / progress.total) * 100) : 0;
  const pct = status === "done" ? 100 : Math.min(99, rawPct);
  const actionLabel = claim ? "Send claim notices" : "Apply";
  const canApply = !!preview && preview.eligible > 0 && status !== "running" && !approval && campaign.status === "live" && campaign.active;

  return (
    <ModalShell title={`Audience: ${campaign.name}`} onClose={onClose} wide>
      <div className="space-y-4">
        <HowItWorks>
          <p>{claim
            ? "Claim mode: 'Send claim notices' creates a pending claim for each eligible user and notifies them. Credits move only when a user taps Claim. The cost shown is the most it could cost."
            : "Admin-apply mode: 'Apply' adds the credits to every eligible user's wallet right away, in batches. Safe to run again - nobody is paid twice."}</p>
          <p>The campaign must be live and active to apply. If the audience changes between preview and confirm, you will be asked to confirm again.</p>
          <p>A large apply (by default 100 or more users, or 10,000 or more credits) is not started at once: it waits for a different admin to approve it in Gamification &gt; Approvals, even when two-admin approval is off.</p>
        </HowItWorks>

        {conflict && <div className="text-sm bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-lg px-3 py-2">{conflict}</div>}
        {approval && (
          <div role="status" className="text-sm bg-indigo-500/10 border border-indigo-500/30 text-indigo-200 rounded-lg px-3 py-2">
            <p className="font-medium">Waiting for approval{approval.status === "executing" ? " (being started now)" : ""}</p>
            <p className="text-xs text-indigo-200/80">
              {approval.summary ?? "This apply"} needs a different admin to approve it in Gamification &gt; Approvals. Nothing has been paid yet.
              {approval.requested_at ? ` Requested ${formatISTDateTime(approval.requested_at)}.` : ""}
              {approval.expires_at ? ` Expires ${formatISTDateTime(approval.expires_at)}.` : ""}
            </p>
            <button className={`${btnGhost} mt-1`} onClick={() => { void loadApproval(); void refreshCampaign(); }}>Check again</button>
          </div>
        )}

        {loading && !preview ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : previewError && !preview ? (
          <ErrorState message={previewError} onRetry={() => void loadPreview()} />
        ) : preview ? (
          <>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span>Apply status:</span> <ApplyStatusBadge status={status} error={fresh.apply_error} />
              {fresh.applied_at && (
                <span className="flex flex-wrap items-center gap-x-1">
                  last applied {formatISTDateTime(fresh.applied_at)}
                  {fresh.applied_by && <>by <PersonCell person={fresh.applied_by_user} fallback="an admin" /></>}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Matched" value={preview.matched} />
              <Stat label="Eligible" value={preview.eligible} tone="text-emerald-400" />
              <Stat label="Already received" value={preview.already_received} tone="text-blue-300" />
              <Stat label={claim ? "Max credits cost" : "Total credits cost"} value={preview.total_credits.toLocaleString("en-IN")} tone="text-amber-300" />
            </div>
            <div>
              <h4 className="text-xs font-medium text-gray-400 mb-1">Skipped by reason</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(preview.skipped).map(([k, n]) => (
                  <Stat key={k} label={SKIP_LABEL[k] ?? k} value={n} tone={n ? "text-red-300" : "text-gray-400"} />
                ))}
              </div>
            </div>
            <div className="text-sm text-gray-300 bg-gray-800/50 border border-gray-800 rounded-lg px-3 py-2">
              Credits by role: sellers {perRole?.seller?.toLocaleString("en-IN") ?? 0}, buyers {perRole?.buyer?.toLocaleString("en-IN") ?? 0}. Total {total.toLocaleString("en-IN")}.
            </div>
            {previewError && <div role="alert" className="text-sm bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg px-3 py-2">{previewError}</div>}
            {preview.sample.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-400 mb-1">Sample of eligible users</h4>
                <ul className="space-y-1">
                  {preview.sample.map((u) => (
                    <li key={u.id} className="text-sm bg-gray-800/40 rounded-lg px-3 py-1.5 flex flex-wrap justify-between gap-x-3">
                      <span className="text-white truncate">{u.name || "(no name)"} <span className="text-gray-500">{u.email}</span></span>
                      <span className="text-xs text-gray-500 capitalize">{u.role}{u.company ? ` · ${u.company}` : ""}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : null}

        {status === "running" && (
          <div>
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>{claim ? "Sending notices…" : "Granting credits…"}</span>
              <span>
                {progress ? `${progress.granted} granted · ${progress.notified} notified · ${progress.failed} failed · ${progress.pending} pending of ${progress.total}` : "Starting…"}
              </span>
            </div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
        {status === "failed" && <p className="text-sm text-red-300">{applyErrorText(fresh.apply_error)} Press {actionLabel} again once fixed; it picks up where it stopped.</p>}
        <Help>Re-running Apply is safe: each user is credited at most once.</Help>
        {campaign.status !== "live" && <Help>Apply is only available while the campaign is live (current status: {campaign.status}).</Help>}

        {confirming && preview && (
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-lg p-3 space-y-2">
            <p className="text-sm text-amber-200 font-medium">
              {claim ? "Notify" : "Grant to"} {users.toLocaleString("en-IN")} users - total {total.toLocaleString("en-IN")} credits{claim ? " at most" : ""}.
            </p>
            <p className="text-xs text-amber-200/80">
              {breakdown ? `${breakdown} = ${total.toLocaleString("en-IN")} credits.` : `${users.toLocaleString("en-IN")} users = ${total.toLocaleString("en-IN")} credits.`}
              {claim ? " Credits are added only when each user claims." : " This adds credits to wallets immediately and cannot be undone."}
            </p>
            <div className="flex gap-2 justify-end">
              <button className={btnGhost} onClick={() => setConfirming(false)} disabled={applying}>Cancel</button>
              <button className={btnPrimary} onClick={doApply} disabled={applying}>
                {applying ? "Working…" : `Confirm ${total.toLocaleString("en-IN")} credits`}
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <button className={btnGhost} onClick={() => void loadPreview()} disabled={loading || status === "running"}>Refresh preview</button>
          <button className={btnGhost} onClick={onClose}>Close</button>
          <button className={btnPrimary} disabled={!canApply || confirming} onClick={() => { setConflict(null); setConfirming(true); }}>
            {actionLabel}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
